import { useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ChapbookDivider } from '../shared/components/ChapbookDivider.js';
import { RolePlate } from '../shared/components/RolePlate.js';
import { SecretWordLabel } from '../shared/components/SecretWordLabel.js';
import { Spread } from '../shared/components/Spread.js';
import { Timer } from '../shared/components/Timer.js';
import { CastGrid } from './components/CastGrid.js';
import { MysteryFlipGrid } from '../shared/components/MysteryFlipGrid.js';
import { MeetingTransition } from './MeetingTransition.js';
import type { MeetingView } from './types.js';
import type { LobbyView } from '../lobby/types.js';
import './GhostMeeting.css';

interface GhostMeetingProps {
  view: MeetingView;
  /**
   * Invoked when the local Ghost viewer taps a cast cell. Ignored on
   * Villager / Fool POV (their cast surface is the non-interactive mystery
   * grid). In production this dispatches `ghostMeetingVote`; in mock-state
   * mode the host stores the vote into the local view's votes map so the
   * voted-state border and ribbon glyphs update.
   */
  onVote?: (targetId: string) => void;
  /**
   * When `true`, the Lobby → GhostMeeting page-turn cinematic plays as an
   * overlay above the meeting. The caller (phase router in production,
   * MockApp in design-review) is responsible for flipping it back to
   * `false` from `onTransitionComplete`.
   */
  transitioning?: boolean;
  onTransitionComplete?: () => void;
  /**
   * Snapshot of the lobby at the moment Begin fired. Required when
   * `transitioning=true` so MeetingTransition can render the lobby's
   * content on the snapshot's verso and the leaf's front face — the
   * page that's being turned literally carries its content. Ignored when
   * not transitioning.
   */
  lobbyView?: LobbyView;
  /**
   * V/F mystery-grid wind-down flag. The phase router flips this to `true`
   * ~600ms before the meeting → clue-giving phase boundary fires, halting
   * new shuffle swaps so any in-flight Framer Motion `layout` transition
   * can ease to a rest. Ignored on Ghost POV (no mystery grid). Default
   * `false`. See `MysteryFlipGrid` mode `'settling'` for details.
   */
  settling?: boolean;
  /**
   * Cold-arrival flag from the phase router: true for the first render after
   * reconnect-mid-meeting or boot-into-meeting, suppressing the cast/vote
   * entry beats. Default `false`. Ignored on V/F POV.
   */
  justArrived?: boolean;
}

/**
 * The Ghost Meeting destination. Verso (left) carries the action
 * (timer, prompt, cast/mystery grid); recto (right) carries the identity
 * (role plate, divider, secret-word sentence).
 *
 * The chapbook chrome — paper scans, twin rules, foliate corner ornaments,
 * spine, corner-wear, drop-shadow — lives in the shared `<Spread>` primitive
 * and is byte-for-byte identical to what the Lobby renders. Chromatic
 * divergence is the only per-POV variation: `pov='ghost'` swaps the
 * `--page-*` token set and adds cool corner vignettes plus a deeper
 * drop-shadow. Same DOM tree → silhouette-identical to the Lobby spread,
 * which is load-bearing for the eventual Lobby → GhostMeeting page-turn
 * cinematic.
 */
export function GhostMeeting({
  view,
  onVote,
  transitioning = false,
  onTransitionComplete,
  lobbyView,
  settling = false,
  justArrived = false,
}: GhostMeetingProps): JSX.Element {
  const { t } = useTranslation();
  /* Voter-by-target index for the Ghost POV: which voter avatars belong in
     each candidate's ribbon. Computed once per view; the cast grid consumes
     it without each cell scanning the votes map itself. */
  const votersByTarget = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const [voterId, targetId] of view.votes) {
      const list = map.get(targetId);
      if (list) list.push(voterId);
      else map.set(targetId, [voterId]);
    }
    return map;
  }, [view.votes]);

  const myVoteTarget = view.votes.get(view.meId);
  const promptKey = view.pov === 'ghost' ? 'meeting.prompt.ghost' : 'meeting.prompt.villager';

  const meetingPov: 'village' | 'ghost' = view.pov === 'ghost' ? 'ghost' : 'village';

  /* Inking beat: when `transitioning` flips from true → false, the page-turn
     cinematic has just settled. Stagger meeting content in over ~900ms so it
     reads as "printed onto the page" rather than slammed into place. Brief
     §7 beat 5: "+200ms after settle: Meeting content inks in."
     useLayoutEffect (not useEffect) so the data-inking='yes' flip lands in
     the SAME paint cycle as transitioning→false. With useEffect there was a
     paint gap where data-transitioning='no' but data-inking='no', revealing
     meeting content at default opacity 1 for one frame before the keyframe
     animation restarted it from 0 — read by users as content fading in
     twice. */
  const [inking, setInking] = useState(false);
  /* Defers the chromatic divergence (warm → cool Silver-Blue) so it lands
     AT the role-reveal moment rather than during the page-turn. While
     false, the Spread renders pov='village' regardless of the actual
     meeting POV; when the role reveals at 3000ms into inking, this flips
     true and the Spread's pov flip drives the CSS palette tween via the
     data-palette-tweening transitions in GhostMeeting.css.
     Initialized from transitioning: a direct mount (e.g. cycler →
     ghost-meeting) skips deferral so the cool palette appears immediately
     without a phantom tween. */
  const [shouldShowFinalPov, setShouldShowFinalPov] = useState(!transitioning);
  const prevTransitioningRef = useRef(transitioning);
  useLayoutEffect(() => {
    const wasTransitioning = prevTransitioningRef.current;
    prevTransitioningRef.current = transitioning;
    if (transitioning && !wasTransitioning) {
      /* New cinematic starting — defer the final POV until role-reveal. */
      setShouldShowFinalPov(false);
    }
    if (wasTransitioning && !transitioning) {
      setInking(true);
      /* 7650ms covers the two-phase reveal in GhostMeeting.css:
           2000ms initial pause →
           preamble (delay 2000, dur 1000, ends 3000) →
           role + divider in parallel (delay 3000, dur 1500, both end 4500) →
           secret-word (delay 4500, dur 2000, ends 6500) →
           300ms breath →
           verso prompt + cast simultaneous (delay 6800, dur 400, end 7200) →
           verso timer (delay 7200, dur 400, ends 7600).
         Held to 7650 so the keyframe's final frame paints before
         data-inking flips back to 'no'. */
      const timer = window.setTimeout(() => setInking(false), 7650);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [transitioning]);

  /* When inking starts, flip the final POV at the role-reveal moment
     (3000ms — same beat the role's keyframe animation starts). The
     CSS palette transitions (1500ms duration, scoped via
     data-palette-tweening='yes') tween the spread page background,
     ink colors, and drop-shadow alongside the role's opacity reveal. */
  useEffect(() => {
    if (!inking) return undefined;
    const timer = window.setTimeout(() => setShouldShowFinalPov(true), 3000);
    return () => window.clearTimeout(timer);
  }, [inking]);

  const effectivePov: 'village' | 'ghost' = shouldShowFinalPov ? meetingPov : 'village';
  /* Palette tween is "armed" throughout the cinematic — CSS transitions
     defined under data-palette-tweening='yes' only FIRE when a property
     value changes, so arming early (during transitioning, before the
     pov flip) doesn't snap anything. It just ensures the transition
     rules are in effect when shouldShowFinalPov flips. */
  const paletteTweening = transitioning || inking;

  return (
    <div
      className="susdle-meeting"
      data-inking={inking ? 'yes' : 'no'}
      data-transitioning={transitioning ? 'yes' : 'no'}
      data-palette-tweening={paletteTweening ? 'yes' : 'no'}
    >
      <h1 className="susdle-sr-only">{t('meeting.ariaLabel')}</h1>
      <Spread
        pov={effectivePov}
        ariaLabel={t('meeting.ariaLabel')}
        verso={
          <div className="susdle-meeting-verso">
            <div className="susdle-meeting-verso__timer">
              <Timer
                phaseEndsAt={view.phaseEndsAt}
                durationMs={view.durationMs}
              />
            </div>
            <p className="susdle-meeting-verso__prompt">{t(promptKey)}</p>
            {view.pov === 'ghost' ? (
              <CastGrid
                players={view.players}
                ghostIds={view.ghostIds}
                meId={view.meId}
                votersByTarget={votersByTarget}
                justArrived={justArrived}
                {...(myVoteTarget !== undefined ? { myVoteTarget } : {})}
                {...(onVote !== undefined ? { onVote } : {})}
              />
            ) : (
              <MysteryFlipGrid
                mode={settling ? 'settling' : 'shuffling'}
                count={view.players.length}
                ariaLabel={t('meeting.mystery.ariaLabel')}
                fill
              />
            )}
          </div>
        }
        recto={
          <div className="susdle-meeting-recto">
            <RolePlate
              preamble={t('meeting.rolePlate.preamble')}
              roleName={t(view.pov === 'ghost' ? 'meeting.rolePlate.ghost' : 'meeting.rolePlate.villager')}
            />
            <ChapbookDivider />
            <div className="susdle-meeting-recto__secret">
              {view.secret.mode === 'word' ? (
                <SecretWordLabel mode="word" word={view.secret.value} />
              ) : (
                <SecretWordLabel mode="length" length={view.secret.value} />
              )}
              {view.pov === 'ghost' ? (
                <p className="susdle-meeting-recto__subline">
                  {t('meeting.rolePlate.subline.ghost')}
                </p>
              ) : null}
            </div>
          </div>
        }
      />
      {transitioning && lobbyView ? (
        <MeetingTransition
          to={meetingPov}
          lobbyView={lobbyView}
          onComplete={onTransitionComplete ?? (() => undefined)}
        />
      ) : null}
    </div>
  );
}
